import React, { useState } from 'react';
import { Calendar, User, Tag, ExternalLink, ArrowLeft, Clock, Share2, Eye, TrendingUp, BookOpen } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedPost, setSelectedPost] = useState(null);

  const categories = ['All', 'Technology', 'Adaptive Sports', 'Community Outreach', 'Gear Innovation'];

  const blogPosts = [
    {
      id: 1,
      title: "The Tech Revolution in Pickleball: Smart Paddles, AI Analytics & 2025 Innovations",
      excerpt: "From NFC-enabled paddles to AI-powered coaching, discover how cutting-edge technology is transforming pickleball training, performance tracking, and player development in 2025.",
      category: "Technology",
      author: "Coach Felix",
      date: "2025-01-08",
      image: "/blog%20photos/tech%20blog.jpg",
      featured: true,
      readTime: "8 min read",
      views: "2.1k",
      tags: ["Smart Technology", "Performance Analytics", "Innovation", "Training"],
      sources: [
        {
          title: "The Evolution Of Pickleball Gear: What's New In 2025?",
          url: "https://www.playpickle.sg/blog/the-evolution-of-pickleball-gear-whats-new-in-2025"
        },
        {
          title: "How Technology & Growth Are Reshaping Pickleball in 2025 & Beyond",
          url: "https://www.centercourtpickleball.com/blog/future-of-pickleball-top-trends-shaping-2025-beyond"
        }
      ],
      content: `
        <div class="prose prose-invert max-w-none">
          <p class="text-xl text-gray-300 leading-relaxed mb-8">Pickleball isn't just growing—it's evolving at lightning speed. As we move through 2025, the sport is experiencing a technological revolution that's reshaping how players train, compete, and connect with the game. From AI-powered coaching to smart paddles that track every shot, innovation is driving pickleball into a new era of performance and accessibility.</p>
          
          <h2 class="text-3xl font-bold text-lime-400 mb-6 mt-12">Next-Generation Paddle Technology: Beyond Traditional Materials</h2>
          <p class="text-gray-300 leading-relaxed mb-6">The paddle industry has undergone a complete transformation in 2025. Gone are the days of basic polymer cores and standard fiberglass faces. Today's paddles feature ultra-lightweight carbon fiber composites, thermoformed bodies, and vibration-dampening edgeguards that optimize both control and power without adding unnecessary weight.</p>
          
          <p class="text-gray-300 leading-relaxed mb-6">Players can now fine-tune their paddles with interchangeable grip systems, balance customizations, and micro-textured surfaces designed to enhance spin without sacrificing feel. The SMARTx COREx4, set to launch in 2025, promises a cutting-edge performance paddle with embedded pressure sensors and accelerometers that track and measure every hit and swing during play.</p>
          
          <h2 class="text-3xl font-bold text-lime-400 mb-6 mt-12">NFC Technology: The Smart Paddle Revolution</h2>
          <p class="text-gray-300 leading-relaxed mb-6">Near Field Communication (NFC) technology is revolutionizing how players interact with their equipment. By embedding NFC chips into paddles, manufacturers are creating a seamless connection between physical gear and digital coaching platforms. Simply tapping a smartphone to your paddle can instantly register equipment, authenticate authenticity, and access personalized training programs.</p>
          
          <div class="bg-gray-800/50 border border-lime-400/20 rounded-lg p-6 my-8">
            <h3 class="text-xl font-bold text-lime-400 mb-4">Key NFC Applications:</h3>
            <ul class="text-gray-300 space-y-2">
              <li>• Instant equipment registration and warranty tracking</li>
              <li>• Real-time access to training videos and tutorials</li>
              <li>• Personalized recommendations based on playing style</li>
              <li>• Maintenance alerts and care reminders</li>
              <li>• Direct links to customer support and services</li>
            </ul>
          </div>
          
          <h2 class="text-3xl font-bold text-lime-400 mb-6 mt-12">Smart Wearables: Data-Driven Performance Enhancement</h2>
          <p class="text-gray-300 leading-relaxed mb-6">Wearable technology has entered the pickleball arena with unprecedented sophistication. Advanced fitness trackers and smartwatches now track swing mechanics, movement patterns, and endurance metrics in real-time. These devices provide instant feedback on shot placement and footwork using motion sensors and AI-based analytics.</p>
          
          <p class="text-gray-300 leading-relaxed mb-6">Heart rate variability data helps players understand their stress levels and recovery status, enabling them to make informed decisions about training intensity. Sleep pattern monitoring optimizes recovery, ensuring players are well-rested for peak performance.</p>
          
          <h2 class="text-3xl font-bold text-lime-400 mb-6 mt-12">AI-Powered Coaching and Video Analysis</h2>
          <p class="text-gray-300 leading-relaxed mb-6">Artificial intelligence is transforming pickleball coaching through platforms like SwingVision, which offers comprehensive game analysis using just a smartphone camera. These AI-powered tools automatically detect and categorize shots, track ball and player movements, and generate instant feedback on speed, spin, and accuracy.</p>
          
          <p class="text-gray-300 leading-relaxed mb-6">The integration of virtual reality and augmented reality training provides immersive skill-building experiences, while smart paddles sync performance data with coaching apps to analyze strengths and weaknesses in detail, recommending specific drills based on collected data.</p>
          
          <h2 class="text-3xl font-bold text-lime-400 mb-6 mt-12">The Future: Connected Courts and eSports Integration</h2>
          <p class="text-gray-300 leading-relaxed mb-6">Looking ahead, smart courts equipped with sensors and cameras will provide instant performance analysis, while the potential for eSports integration opens new avenues for competition and engagement. Virtual tournaments and online leagues are already creating global communities of players who can compete regardless of geographical location.</p>
          
          <p class="text-gray-300 leading-relaxed mb-6">As we continue through 2025, these technological innovations are not just enhancing performance—they're making pickleball more accessible, enjoyable, and connected than ever before. The future of pickleball is digital, data-driven, and designed for players of all abilities.</p>
        </div>
      `
    },
    {
      id: 2,
      title: "Breaking Barriers: The Rise of Adaptive & Wheelchair Pickleball",
      excerpt: "Explore how adaptive pickleball is creating inclusive communities, breaking down accessibility barriers, and empowering players of all abilities to thrive on the court.",
      category: "Adaptive Sports",
      author: "Dr. Maria Rodriguez",
      date: "2025-01-05",
      image: "/blog%20photos/breaking%20barrier%20blog.jpg",
      featured: false,
      readTime: "6 min read",
      views: "1.8k",
      tags: ["Accessibility", "Inclusion", "Adaptive Rules", "Community"],
      sources: [
        {
          title: "USA Pickleball Official Adaptive/Wheelchair Rules",
          url: "https://usapickleball.org/wheelchair-pickleball/"
        },
        {
          title: "Considerations for Designing a Wheelchair Pickleball Court",
          url: "https://northstateresurfacing.com/blog/wheelchair-pickleball-court-deasign"
        }
      ],
      content: `
        <div class="prose prose-invert max-w-none">
          <p class="text-xl text-gray-300 leading-relaxed mb-8">Adaptive pickleball is experiencing unprecedented growth, proving that this sport truly embodies the principle of "pickleball for all." With thoughtful rule modifications and innovative court designs, players with disabilities are not just participating—they're excelling, competing, and building vibrant communities that are reshaping the landscape of inclusive sports.</p>
          
          <h2 class="text-3xl font-bold text-lime-400 mb-6 mt-12">Understanding Adaptive Pickleball: More Than Just Rule Changes</h2>
          <p class="text-gray-300 leading-relaxed mb-6">Adaptive pickleball encompasses players with various types of disabilities, not just those using wheelchairs. This includes individuals with visual impairments, hearing disabilities, mobility constraints, neurological conditions, and other physical disabilities. The beauty of adaptive pickleball lies in its flexibility to accommodate diverse needs while maintaining the competitive spirit of the game.</p>
          
          <div class="bg-gray-800/50 border border-lime-400/20 rounded-lg p-6 my-8">
            <h3 class="text-xl font-bold text-lime-400 mb-4">Essential Adaptive Rules:</h3>
            <ul class="text-gray-300 space-y-3">
              <li><strong>Double Bounce Rule:</strong> Players using wheelchairs may allow the ball to bounce twice before returning it. The second bounce can occur anywhere on the playing surface.</li>
              <li><strong>Serving Position:</strong> Both rear wheels must be behind the baseline when serving, positioned between the imaginary extensions of the sideline and centerline.</li>
              <li><strong>Non-Volley Zone:</strong> Front (smaller) wheels may touch the kitchen during a volley, but rear wheels must be established outside the zone before volleying.</li>
              <li><strong>Three-Bounce Fault:</strong> Failure to return the ball before it bounces three times results in a fault.</li>
            </ul>
          </div>
          
          <h2 class="text-3xl font-bold text-lime-400 mb-6 mt-12">Building Inclusive Communities</h2>
          <p class="text-gray-300 leading-relaxed mb-6">One of pickleball's greatest strengths is its ability to bring together players of different abilities on the same court. Mixed-ability play, where wheelchair users compete alongside standing players, creates unique learning opportunities and breaks down barriers between disabled and non-disabled athletes.</p>
          
          <p class="text-gray-300 leading-relaxed mb-6">The future of adaptive pickleball is bright, with continued growth in participation, expanding tournament opportunities, and ongoing innovations in equipment and court design. As more communities embrace these inclusive practices, pickleball is truly becoming a sport for all.</p>
        </div>
      `
    },
    {
      id: 3,
      title: "Pickleball for All: Community Outreach Programs Changing Lives",
      excerpt: "Discover how pickleball outreach initiatives are building stronger communities, supporting youth development, and creating inclusive spaces for players from all backgrounds.",
      category: "Community Outreach",
      author: "Sarah Chen",
      date: "2025-01-03",
      image: "/blog%20photos/community%20outreach.jpg",
      featured: false,
      readTime: "7 min read",
      views: "2.3k",
      tags: ["Community Building", "Youth Programs", "Diversity", "Social Impact"],
      sources: [
        {
          title: "USA Pickleball Serves 'Play It Forward' Grant Program",
          url: "https://usapickleball.org/community-relations/usa-pickleball-serves-announces-play-it-forward-grant-program/"
        },
        {
          title: "USA Pickleball Serves Community Impact",
          url: "https://usapickleball.org/serves/"
        }
      ],
      content: `
        <div class="prose prose-invert max-w-none">
          <p class="text-xl text-gray-300 leading-relaxed mb-8">Pickleball is more than just America's fastest-growing sport—it's become a powerful catalyst for positive social change. Through innovative community outreach programs, dedicated grant initiatives, and grassroots efforts, pickleball is breaking down barriers, building bridges between diverse communities, and creating opportunities for people of all backgrounds to thrive.</p>
          
          <h2 class="text-3xl font-bold text-lime-400 mb-6 mt-12">USA Pickleball Serves: Leading the Charge</h2>
          <p class="text-gray-300 leading-relaxed mb-6">USA Pickleball Serves, the charitable arm of USA Pickleball, exemplifies how sports organizations can drive meaningful community impact. Their mission goes beyond promoting the game—they're dedicated to creating positive national impact through court enhancements, educational youth programming, and inclusive play initiatives.</p>
          
          <div class="bg-gray-800/50 border border-lime-400/20 rounded-lg p-6 my-8">
            <h3 class="text-xl font-bold text-lime-400 mb-4">Grant Requirements:</h3>
            <ul class="text-gray-300 space-y-2">
              <li>• Courts must be installed in public facilities</li>
              <li>• Permanent, dedicated pickleball courts required</li>
              <li>• Installation and opening within 12 months of funding</li>
              <li>• Collaboration with local USA Pickleball Ambassadors</li>
              <li>• Permanent USA Pickleball Serves branding and ribbon-cutting ceremony</li>
            </ul>
          </div>
          
          <h2 class="text-3xl font-bold text-lime-400 mb-6 mt-12">Youth Development: Building Tomorrow's Leaders</h2>
          <p class="text-gray-300 leading-relaxed mb-6">Youth programming represents a cornerstone of pickleball's community outreach efforts. These programs go beyond teaching sport fundamentals. They develop crucial life skills including teamwork, strategic thinking, communication, and perseverance. The low-impact nature of pickleball makes it accessible to youth of all physical abilities, while its social aspects help combat isolation and build confidence.</p>
          
          <p class="text-gray-300 leading-relaxed mb-6">As we look toward the future, pickleball's commitment to accessibility, inclusion, and community building positions it not just as a sport, but as a movement—one that's creating a more connected, understanding, and empowered world through the simple joy of play.</p>
        </div>
      `
    },
    {
      id: 4,
      title: "Building Inclusive Courts: A Community Guide",
      excerpt: "Everything you need to know about making your local pickleball courts accessible for players of all abilities.",
      category: "Adaptive Sports",
      author: "Maria Rodriguez",
      date: "2023-12-28",
      image: "/blog%20photos/building%20inclusive.jpg",
      featured: false,
      readTime: "5 min read",
      views: "1.5k",
      tags: ["Court Design", "Accessibility", "Community Planning"],
      content: `
        <div class="prose prose-invert max-w-none">
          <p class="text-xl text-gray-300 leading-relaxed mb-8">Creating inclusive pickleball courts requires thoughtful planning, community input, and adherence to accessibility standards. This comprehensive guide walks through the essential elements of designing courts that welcome players of all abilities.</p>
          
          <h2 class="text-3xl font-bold text-lime-400 mb-6 mt-12">Design Fundamentals</h2>
          <p class="text-gray-300 leading-relaxed mb-6">Proper court design is crucial for adaptive pickleball success. While standard pickleball courts measure 44 feet long by 20 feet wide, the recommended playing surface area for wheelchair play requires additional space for safe maneuvering and optimal gameplay experience.</p>
          
          <p class="text-gray-300 leading-relaxed mb-6">Key considerations include level surfaces, proper drainage, slip-resistant materials, and clear pathways that meet ADA compliance standards for maximum accessibility and safety.</p>
        </div>
      `
    }
  ];

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const FeaturedBlogCard = ({ post }) => (
    <Card 
      className="bg-gradient-to-br from-gray-900/95 to-gray-800/95 border-lime-400/30 overflow-hidden group hover:border-lime-400/60 transition-all duration-500 backdrop-blur-sm cursor-pointer shadow-2xl hover:shadow-lime-400/20"
      onClick={() => setSelectedPost(post)}
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <img 
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        <div className="absolute top-4 left-4">
          <span className="bg-lime-400 text-black px-4 py-2 rounded-full text-sm font-bold shadow-lg">
            {post.category}
          </span>
        </div>
        
        <div className="absolute top-4 right-4">
          <div className="bg-black/20 backdrop-blur-sm rounded-full p-2">
            <TrendingUp className="w-5 h-5 text-lime-400" />
          </div>
        </div>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center space-x-4 text-white/90 text-sm">
            <div className="flex items-center bg-black/30 backdrop-blur-sm rounded-full px-3 py-1">
              <Eye className="w-4 h-4 mr-2" />
              {post.views}
            </div>
            <div className="flex items-center bg-black/30 backdrop-blur-sm rounded-full px-3 py-1">
              <Clock className="w-4 h-4 mr-2" />
              {post.readTime}
            </div>
          </div>
        </div>
      </div>
      
      <CardContent className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div className="flex items-center space-x-2 sm:space-x-4 text-gray-400">
            <div className="flex items-center">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="text-xs sm:text-sm">{post.date}</span>
            </div>
            <div className="flex items-center">
              <User className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="text-xs sm:text-sm font-medium">{post.author}</span>
            </div>
          </div>
          <Button size="sm" className="bg-lime-400/10 text-lime-400 hover:bg-lime-400/20 border-lime-400/30 p-2">
            <Share2 className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
        </div>
        
        <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-black text-white mb-3 sm:mb-4 group-hover:text-lime-400 transition-colors leading-tight">
          {post.title}
        </h2>
        
        <p className="text-gray-300 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base lg:text-lg">
          {post.excerpt}
        </p>

        <div className="flex flex-wrap gap-1 sm:gap-2 mb-4 sm:mb-6">
          {post.tags?.slice(0, 4).map((tag, index) => (
            <span key={index} className="bg-gray-700/50 text-lime-400 px-2 sm:px-3 py-1 rounded-full text-xs font-medium hover:bg-lime-400/10 transition-colors">
              #{tag}
            </span>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
          <Button className="bg-lime-400 text-black hover:bg-lime-300 font-bold px-4 sm:px-6 py-2 sm:py-3 rounded-xl shadow-lg hover:shadow-lime-400/30 transition-all duration-300 text-sm sm:text-base w-full sm:w-auto">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Read Full Story
          </Button>
          {post.sources && (
            <div className="flex items-center text-gray-400 text-xs sm:text-sm bg-gray-800/50 rounded-full px-2 sm:px-3 py-1 sm:py-2">
              <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              {post.sources.length} Research Sources
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const CompactBlogCard = ({ post }) => (
    <Card 
      className="bg-gray-900/40 border-gray-700/50 overflow-hidden group hover:border-lime-400/50 transition-all duration-300 backdrop-blur-sm cursor-pointer hover:bg-gray-900/60"
      onClick={() => setSelectedPost(post)}
    >
      <div className="flex">
        <div className="w-24 sm:w-32 lg:w-40 aspect-square overflow-hidden relative flex-shrink-0">
          <img 
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          <div className="absolute top-1 sm:top-2 left-1 sm:left-2">
            <span className="bg-lime-400 text-black px-1 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-bold">
              {post.category}
            </span>
          </div>
        </div>
        
        <div className="flex-1 p-3 sm:p-4 min-w-0">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-2 text-xs text-gray-400 overflow-hidden">
            <div className="flex items-center flex-shrink-0">
              <Calendar className="w-3 h-3 mr-1" />
              <span className="truncate">{post.date}</span>
            </div>
            <div className="flex items-center flex-shrink-0">
              <Eye className="w-3 h-3 mr-1" />
              <span>{post.views}</span>
            </div>
            <div className="hidden sm:flex items-center flex-shrink-0">
              <Clock className="w-3 h-3 mr-1" />
              <span>{post.readTime}</span>
            </div>
          </div>
          
          <h4 className="text-white font-bold text-sm sm:text-base leading-tight mb-2 group-hover:text-lime-400 transition-colors line-clamp-2">
            {post.title}
          </h4>
          
          <p className="text-gray-400 text-xs sm:text-sm leading-relaxed line-clamp-2 mb-3">
            {post.excerpt}
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center text-xs text-gray-500 min-w-0 flex-1 mr-2">
              <User className="w-3 h-3 mr-1 flex-shrink-0" />
              <span className="truncate">{post.author}</span>
            </div>
            <Button size="sm" className="bg-lime-400/10 text-lime-400 hover:bg-lime-400/20 text-xs px-2 sm:px-3 py-1 h-6 sm:h-7 flex-shrink-0">
              Read More
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );

  const FullBlogPost = ({ post }) => (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <Button 
          onClick={() => setSelectedPost(null)}
          className="bg-gray-800/80 text-white hover:bg-gray-700 mb-6 backdrop-blur-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Articles
        </Button>
        
        <div className="mb-6">
          <span className="bg-lime-400 text-black px-4 py-2 rounded-full text-sm font-bold">
            {post.category}
          </span>
        </div>
        
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
          {post.title}
        </h1>
        
        <div className="flex items-center space-x-6 mb-8 text-gray-400">
          <div className="flex items-center">
            <User className="w-5 h-5 mr-2" />
            <span className="font-medium">{post.author}</span>
          </div>
          <div className="flex items-center">
            <Calendar className="w-5 h-5 mr-2" />
            {post.date}
          </div>
          <div className="flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            {post.readTime}
          </div>
          <div className="flex items-center">
            <Eye className="w-5 h-5 mr-2" />
            {post.views} views
          </div>
          <Button className="bg-lime-400/10 text-lime-400 hover:bg-lime-400/20 p-2">
            <Share2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8 shadow-2xl">
        <img 
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
      </div>
      
      <div 
        className="prose prose-invert prose-lime max-w-none prose-lg"
        dangerouslySetInnerHTML={{ __html: post.content }}
      />
      
      {post.sources && (
        <div className="mt-12 p-8 bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-lime-400/20 rounded-2xl backdrop-blur-sm">
          <h3 className="text-2xl font-bold text-lime-400 mb-6 flex items-center">
            <ExternalLink className="w-6 h-6 mr-3" />
            Research Sources & Further Reading
          </h3>
          <div className="grid gap-4">
            {post.sources.map((source, index) => (
              <a 
                key={index}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 bg-gray-800/30 rounded-lg hover:bg-gray-700/30 transition-colors group border border-gray-700/50 hover:border-lime-400/30"
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 group-hover:text-lime-400 transition-colors font-medium">{source.title}</span>
                  <ExternalLink className="w-4 h-4 text-lime-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
      
      <div className="mt-12 p-8 bg-gradient-to-br from-lime-400/10 to-purple-600/10 border border-lime-400/20 rounded-2xl">
        <div className="flex items-start space-x-4">
          <div className="w-16 h-16 bg-lime-400 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-black font-black text-xl">PBX</span>
          </div>
          <div>
            <h3 className="text-xl font-bold text-white mb-3">About PBX Pickle</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              PBX Pickle is committed to making adaptive pickleball accessible to everyone. Through innovative gear design, 
              community partnerships, and inclusive programming, we're breaking down barriers and building up communities. 
              Join our mission to create a more inclusive sporting world.
            </p>
            <Button className="bg-lime-400 text-black hover:bg-lime-300 font-bold px-6 py-3 rounded-xl">
              Learn More About PBX
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  if (selectedPost) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navigation />
        <section className="pt-24 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FullBlogPost post={selectedPost} />
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-black via-gray-900/50 to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-lime-400/5 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-flex items-center bg-lime-400/10 border border-lime-400/20 rounded-full px-6 py-3 mb-8 backdrop-blur-sm">
              <BookOpen className="w-5 h-5 mr-3 text-lime-400" />
              <span className="text-lime-400 font-bold text-sm uppercase tracking-wider">PBX INSIGHTS</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-8xl font-black text-white mb-8 tracking-tight uppercase leading-none">
              PBX <span className="text-lime-400 bg-gradient-to-r from-lime-400 to-green-400 bg-clip-text text-transparent">STORIES</span>
            </h1>
            
            <p className="text-xl sm:text-2xl lg:text-3xl text-gray-300 max-w-5xl mx-auto leading-relaxed font-light">
              Cutting-edge tech insights, adaptive sports breakthroughs, and community stories from the world of 
              <span className="text-lime-400 font-semibold"> inclusive pickleball</span>
            </p>
            
            <div className="mt-8 flex items-center justify-center space-x-8 text-gray-400">
              <div className="flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-lime-400" />
                <span>Weekly Insights</span>
              </div>
              <div className="flex items-center">
                <Eye className="w-5 h-5 mr-2 text-lime-400" />
                <span>6.2k+ Readers</span>
              </div>
              <div className="flex items-center">
                <ExternalLink className="w-5 h-5 mr-2 text-lime-400" />
                <span>Research-Based</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                className={selectedCategory === category 
                  ? "bg-lime-400 text-black hover:bg-lime-300 font-bold px-6 py-3 rounded-full shadow-lg shadow-lime-400/20 border-0"
                  : "border-gray-600/50 text-gray-300 hover:border-lime-400/50 hover:text-lime-400 hover:bg-lime-400/5 bg-transparent font-medium px-6 py-3 rounded-full backdrop-blur-sm"
                }
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            {/* Featured Post */}
            {filteredPosts.find(post => post.featured) && (
              <div className="lg:col-span-8">
                <div className="mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-white uppercase tracking-wide flex items-center">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-lime-400" />
                    Featured Story
                        </h2>
                          </div>
                <FeaturedBlogCard post={filteredPosts.find(post => post.featured)} />
              </div>
            )}

            {/* Recent Posts Sidebar */}
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 space-y-6 lg:space-y-8">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 uppercase tracking-wide flex items-center">
                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-lime-400" />
                    Latest Insights
                  </h3>
                  <div className="space-y-3 sm:space-y-4">
                  {filteredPosts.filter(post => !post.featured).map((post) => (
                      <CompactBlogCard key={post.id} post={post} />
                    ))}
                  </div>
                </div>

                {/* Newsletter Signup */}
                <Card className="bg-gradient-to-br from-lime-400/10 via-lime-400/5 to-purple-600/10 border border-lime-400/30 backdrop-blur-sm">
                  <CardContent className="p-4 sm:p-6">
                    <div className="text-center mb-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-lime-400 rounded-full flex items-center justify-center mx-auto mb-3">
                        <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                      </div>
                      <h4 className="text-base sm:text-lg font-bold text-white mb-2">Stay Updated</h4>
                      <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                        Get the latest insights on adaptive pickleball, technology innovations, and community impact delivered weekly.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <input
                        type="email"
                        placeholder="Enter your email address"
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:border-lime-400 focus:outline-none text-sm backdrop-blur-sm"
                      />
                      <Button className="w-full bg-lime-400 text-black hover:bg-lime-300 font-bold py-2 sm:py-3 rounded-xl shadow-lg hover:shadow-lime-400/30 transition-all duration-300 text-sm sm:text-base">
                        Subscribe Now
                      </Button>
                          </div>
                    <p className="text-xs text-gray-400 text-center mt-3">
                      Join 6,200+ readers • Unsubscribe anytime
                    </p>
                  </CardContent>
                </Card>

                {/* Research Sources */}
                <Card className="bg-gray-900/40 border-gray-700/50 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-white mb-4 flex items-center">
                      <ExternalLink className="w-5 h-5 mr-3 text-lime-400" />
                      Research Sources
                            </h4>
                    <div className="space-y-3">
                      <a href="https://usapickleball.org/wheelchair-pickleball/" target="_blank" rel="noopener noreferrer" className="block p-3 bg-gray-800/30 rounded-lg hover:bg-gray-700/30 transition-colors group">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-300 group-hover:text-lime-400 text-sm transition-colors">USA Pickleball Adaptive Rules</span>
                          <ExternalLink className="w-4 h-4 text-lime-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                      </a>
                      <a href="https://www.centercourtpickleball.com/blog/" target="_blank" rel="noopener noreferrer" className="block p-3 bg-gray-800/30 rounded-lg hover:bg-gray-700/30 transition-colors group">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-300 group-hover:text-lime-400 text-sm transition-colors">Future of Pickleball Technology</span>
                          <ExternalLink className="w-4 h-4 text-lime-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                      </a>
                      <a href="https://usapickleball.org/serves/" target="_blank" rel="noopener noreferrer" className="block p-3 bg-gray-800/30 rounded-lg hover:bg-gray-700/30 transition-colors group">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-300 group-hover:text-lime-400 text-sm transition-colors">USA Pickleball Community Serves</span>
                          <ExternalLink className="w-4 h-4 text-lime-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </a>
                        </div>
                      </CardContent>
                    </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
